/**
 * Structured Logger for debugging
 *
 * Provides a configurable logging system with:
 * - Multiple log levels (debug, info, warn, error)
 * - Structured output with timestamp, level, and context
 * - Production-safe mode (minimal output)
 * - Module context support
 * - Performance timing helpers
 * - Grouped logging for correlated logs
 */

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  context: string;
  message: string;
  data?: unknown;
  stack?: string;
}

export interface LoggerConfig {
  /** Minimum log level to output */
  level: LogLevel;
  /** Enable pretty printing (dev mode) */
  prettyPrint: boolean;
  /** Enable console output */
  enabled: boolean;
  /** Custom output handler for extending functionality */
  outputHandler?: (entry: LogEntry) => void;
}

export interface Logger {
  debug(message: string, data?: unknown): void;
  info(message: string, data?: unknown): void;
  warn(message: string, data?: unknown): void;
  error(message: string, errorOrData?: Error | unknown): void;
  time(label: string): void;
  timeEnd(label: string): void;
  group(label: string): void;
  groupEnd(): void;
  child(subContext: string): Logger;
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  silent: 4,
};

const LOG_LEVEL_COLORS: Record<Exclude<LogLevel, 'silent'>, string> = {
  debug: '\x1b[36m', // Cyan
  info: '\x1b[32m',  // Green
  warn: '\x1b[33m',  // Yellow
  error: '\x1b[31m', // Red
};

const RESET_COLOR = '\x1b[0m';
const BOLD = '\x1b[1m';
const DIM = '\x1b[2m';

// -----------------------------------------------------------------------------
// Configuration
// -----------------------------------------------------------------------------

const isProduction = (): boolean => {
  // Check various environment indicators
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'production';
  }
  // Browser environment check
  if (typeof window !== 'undefined') {
    return (window as { __PRODUCTION__?: boolean }).__PRODUCTION__ === true;
  }
  return false;
};

const defaultConfig: LoggerConfig = {
  level: isProduction() ? 'warn' : 'debug',
  prettyPrint: !isProduction(),
  enabled: true,
  outputHandler: undefined,
};

let globalConfig: LoggerConfig = { ...defaultConfig };

// -----------------------------------------------------------------------------
// Configuration API
// -----------------------------------------------------------------------------

/**
 * Configure the global logger settings
 */
export function configureLogger(config: Partial<LoggerConfig>): void {
  globalConfig = { ...globalConfig, ...config };
}

/**
 * Reset logger configuration to defaults
 */
export function resetLoggerConfig(): void {
  globalConfig = { ...defaultConfig };
}

/**
 * Get current logger configuration
 */
export function getLoggerConfig(): Readonly<LoggerConfig> {
  return { ...globalConfig };
}

/**
 * Temporarily disable all logging
 */
export function disableLogging(): void {
  globalConfig.enabled = false;
}

/**
 * Re-enable logging
 */
export function enableLogging(): void {
  globalConfig.enabled = true;
}

/**
 * Set the minimum log level
 */
export function setLogLevel(level: LogLevel): void {
  globalConfig.level = level;
}

// -----------------------------------------------------------------------------
// Formatting
// -----------------------------------------------------------------------------

function formatTimestamp(): string {
  return new Date().toISOString();
}

function formatCompact(entry: LogEntry): string {
  const parts = [
    entry.timestamp,
    `[${entry.level.toUpperCase()}]`,
    `[${entry.context}]`,
    entry.message,
  ];

  if (entry.data !== undefined) {
    parts.push(JSON.stringify(entry.data));
  }

  if (entry.stack) {
    parts.push(`\n${entry.stack}`);
  }

  return parts.join(' ');
}

function formatPretty(entry: LogEntry): string[] {
  const color = LOG_LEVEL_COLORS[entry.level as Exclude<LogLevel, 'silent'>] || '';
  const levelPadded = entry.level.toUpperCase().padEnd(5);

  const header = [
    `${DIM}${entry.timestamp}${RESET_COLOR}`,
    `${color}${BOLD}${levelPadded}${RESET_COLOR}`,
    `${DIM}[${entry.context}]${RESET_COLOR}`,
    entry.message,
  ].join(' ');

  const output: string[] = [header];

  if (entry.data !== undefined) {
    output.push(entry.data as string);
  }

  if (entry.stack) {
    output.push(`${DIM}${entry.stack}${RESET_COLOR}`);
  }

  return output;
}

// -----------------------------------------------------------------------------
// Output
// -----------------------------------------------------------------------------

function shouldLog(level: LogLevel): boolean {
  if (!globalConfig.enabled) return false;
  if (level === 'silent') return false;
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[globalConfig.level];
}

function output(entry: LogEntry): void {
  // Custom handler takes precedence
  if (globalConfig.outputHandler) {
    globalConfig.outputHandler(entry);
    return;
  }

  const consoleMethod = entry.level === 'debug' ? 'log' : entry.level;

  if (globalConfig.prettyPrint) {
    const formatted = formatPretty(entry);
    if (formatted.length === 1) {
      console[consoleMethod](formatted[0]);
    } else {
      // First element is the header, rest are data/stack
      const [header, ...rest] = formatted;
      console[consoleMethod](header, ...rest);
    }
  } else {
    console[consoleMethod](formatCompact(entry));
  }
}

// -----------------------------------------------------------------------------
// Timer Storage
// -----------------------------------------------------------------------------

const timers = new Map<string, number>();

// -----------------------------------------------------------------------------
// Logger Factory
// -----------------------------------------------------------------------------

/**
 * Create a logger instance with the specified context
 *
 * @param context - Module or component name for log identification
 * @returns Logger instance
 *
 * @example
 * const logger = createLogger('AuthService');
 * logger.debug('Checking credentials', { userId: 123 });
 * logger.info('User authenticated');
 * logger.warn('Session expiring soon');
 * logger.error('Authentication failed', new Error('Invalid token'));
 */
export function createLogger(context: string): Logger {
  const createLogMethod =
    (level: Exclude<LogLevel, 'silent'>) =>
    (message: string, data?: unknown): void => {
      if (!shouldLog(level)) return;

      const entry: LogEntry = {
        timestamp: formatTimestamp(),
        level,
        context,
        message,
      };

      // Handle error objects specially
      if (level === 'error' && data instanceof Error) {
        entry.data = {
          name: data.name,
          message: data.message,
        };
        entry.stack = data.stack;
      } else if (data !== undefined) {
        entry.data = data;
      }

      output(entry);
    };

  const logger: Logger = {
    debug: createLogMethod('debug'),
    info: createLogMethod('info'),
    warn: createLogMethod('warn'),
    error: createLogMethod('error'),

    /**
     * Start a performance timer
     * @param label - Unique identifier for the timer
     */
    time(label: string): void {
      if (!shouldLog('debug')) return;
      const timerKey = `${context}:${label}`;
      timers.set(timerKey, performance.now());

      if (globalConfig.prettyPrint) {
        console.log(
          `${DIM}${formatTimestamp()}${RESET_COLOR} ${LOG_LEVEL_COLORS.debug}${BOLD}TIMER${RESET_COLOR} ${DIM}[${context}]${RESET_COLOR} ${label} started`
        );
      }
    },

    /**
     * End a performance timer and log the duration
     * @param label - Identifier matching a previous time() call
     */
    timeEnd(label: string): void {
      if (!shouldLog('debug')) return;
      const timerKey = `${context}:${label}`;
      const startTime = timers.get(timerKey);

      if (startTime === undefined) {
        logger.warn(`Timer "${label}" does not exist`);
        return;
      }

      const duration = performance.now() - startTime;
      timers.delete(timerKey);

      const entry: LogEntry = {
        timestamp: formatTimestamp(),
        level: 'debug',
        context,
        message: `${label} completed`,
        data: { durationMs: Math.round(duration * 100) / 100 },
      };

      output(entry);
    },

    /**
     * Start a grouped log section
     * @param label - Group label
     */
    group(label: string): void {
      if (!shouldLog('debug')) return;

      if (globalConfig.prettyPrint) {
        console.group(
          `${DIM}${formatTimestamp()}${RESET_COLOR} ${LOG_LEVEL_COLORS.info}${BOLD}GROUP${RESET_COLOR} ${DIM}[${context}]${RESET_COLOR} ${label}`
        );
      } else {
        console.group(`[${context}] ${label}`);
      }
    },

    /**
     * End the current grouped log section
     */
    groupEnd(): void {
      if (!shouldLog('debug')) return;
      console.groupEnd();
    },

    /**
     * Create a child logger with extended context
     * @param subContext - Additional context to append
     * @returns New logger instance with combined context
     *
     * @example
     * const logger = createLogger('Service');
     * const childLogger = logger.child('SubModule');
     * // Logs will show context as "Service:SubModule"
     */
    child(subContext: string): Logger {
      return createLogger(`${context}:${subContext}`);
    },
  };

  return logger;
}

// -----------------------------------------------------------------------------
// Convenience Exports
// -----------------------------------------------------------------------------

/**
 * Default application logger
 * Use this for general application-wide logging
 */
export const appLogger = createLogger('App');

/**
 * Utility function to wrap async operations with timing
 *
 * @param logger - Logger instance to use
 * @param label - Label for the operation
 * @param fn - Async function to execute
 * @returns Result of the async function
 *
 * @example
 * const result = await withTiming(logger, 'fetchData', async () => {
 *   return await api.getData();
 * });
 */
export async function withTiming<T>(
  logger: Logger,
  label: string,
  fn: () => Promise<T>
): Promise<T> {
  logger.time(label);
  try {
    const result = await fn();
    logger.timeEnd(label);
    return result;
  } catch (error) {
    logger.timeEnd(label);
    throw error;
  }
}

/**
 * Create a scoped logger that automatically groups related logs
 *
 * @param logger - Logger instance to use
 * @param label - Group label
 * @param fn - Function to execute within the group
 * @returns Result of the function
 *
 * @example
 * withGroup(logger, 'Initialization', () => {
 *   logger.info('Step 1');
 *   logger.info('Step 2');
 * });
 */
export function withGroup<T>(
  logger: Logger,
  label: string,
  fn: () => T
): T {
  logger.group(label);
  try {
    return fn();
  } finally {
    logger.groupEnd();
  }
}

/**
 * Async version of withGroup
 */
export async function withGroupAsync<T>(
  logger: Logger,
  label: string,
  fn: () => Promise<T>
): Promise<T> {
  logger.group(label);
  try {
    return await fn();
  } finally {
    logger.groupEnd();
  }
}
